import { Edit } from "@mui/icons-material";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { businessesApi, locationsApi } from "src/api";
import { useAppDispatch } from "src/app/hooks";
import { AppDispatch } from "src/app/store";
import { ColorField } from "src/components/common/ColorField";
import { DropZoneModal } from "src/components/common/DropZoneModal";
import { RoleEnum } from "src/enums";
import { Can } from "src/permissions/Can";
import { preloaderChangeStatus, updateUserPhoto } from "src/stores/common/commonSlice";
import { blankPhoto } from "src/utils/consts";
import {
  detailsWrapperSx,
  buttonImageWrapper,
  imageButton,
  detailsInnerSx,
  imageWrapperSx,
  detailsTitleSx,
  detailItemSx,
  buttonImageWrapperSx,
  imageButtonSx,
  imageInnerSx,
  detailCellSx
} from "./BusinessDetails.sx";
import { BusinessDetailsType } from "./BusinessDetails.types";
import { EditIcon } from "../../icons/EditIcon";
import AMPModal from "../../common/AMPModal";
import { EditBusinessForm } from "../../forms/EditBusinessForm";
import { isErrorObject } from "src/utils/helper";
import { NotifyError } from "src/utils/notification";
import { BusinessResetWrapper } from "../BusinessResetWrapper";

type AddImageMutationTrigger = ReturnType<
  typeof businessesApi.useAddBusinessLogoMutation
>[0];

const handleTrigger = async (
  file: File[],
  businessId: number,
  trigger: AddImageMutationTrigger,
  dispatch: AppDispatch,
  handleCloseImageModal: () => void,
  handleBlockSubmit: (status: boolean) => void
) => {
  try {
    handleBlockSubmit(true)
    dispatch(preloaderChangeStatus(true));
    const formData = new FormData()
    formData.append('file', file[0])
    const response = await trigger({ formData, businessId }).unwrap()
    dispatch(updateUserPhoto(response.logo))
  } catch (error) {
    if(isErrorObject(error)){
      NotifyError(error.data.message)
    }
  } finally {
    handleCloseImageModal()
    dispatch(preloaderChangeStatus(false));
    handleBlockSubmit(false)
  }
};

export const BusinessDetails: FC<BusinessDetailsType> = ({ data }) => {
  const intl = useIntl();
  const [openDropZone, setOpenDropZone] = useState<boolean>(false)
  const [userImage, setUserImage] = useState<File[] | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  
  const [getLocations, locations] = locationsApi.useLazyGetLocationsQuery()
  const [trigger] = businessesApi.useAddBusinessLogoMutation();
  const [blockSubmit,setBlockSubmit] = useState(false)
  
  const handleBlockSubmit = (status: boolean) => {
    setBlockSubmit(status)
  }

  useEffect(() => {
    getLocations({data: { businessId: data.id }})
  }, [])

  const uploadDialogTitle = intl.formatMessage({id: 'addNewImage'})

	
  const dispatch = useAppDispatch();

  const handleOpenImageModal = () => {
    setOpenDropZone(true)
  }

  const handleCloseImageModal = () => {
    setOpenDropZone(false)
  }

  const handleSetImage = (file: File[] | null) => {
    setUserImage(file)
  }

  const handleSubmit = () => {
    userImage && handleTrigger(userImage, data.id, trigger, dispatch, handleCloseImageModal, handleBlockSubmit)
  }
  
  const handleShowEditModal = (status: boolean) => () => setShowEditModal(status)
  
  return (
    <Box sx={detailsWrapperSx}>
      <Grid
        container
        spacing={0}
        sx={detailsWrapperSx}
      >
        <Grid
          item
          xs={12}
          container
          spacing={2}
          sx={detailsInnerSx}
        >
          <Grid
            item
            xs={12}
            md={4}
            sx={imageWrapperSx}
          >
            <Box sx={imageInnerSx}>
              <Can
                I="manage"
                a="business"
              >
                <Box sx={buttonImageWrapperSx}>
                  <IconButton
                    onClick={handleOpenImageModal}
                    sx={imageButtonSx}
                  >
                    <Edit />
                  </IconButton>
                </Box>
              </Can>
              <DropZoneModal
                submitIsBlocked={blockSubmit}
                handleClose={handleCloseImageModal}
                open={openDropZone}
                title={uploadDialogTitle}
                handleFile={handleSetImage}
                handleSubmit={handleSubmit}
                isImage
              />
              <img
                src={data?.logo?.url ?? blankPhoto}
                alt={data?.name}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{position: "relative"}}
          >
            <Box sx={buttonImageWrapper}>
              <IconButton
                onClick={handleShowEditModal(true)}
                sx={imageButton}
              >
                <EditIcon/>
              </IconButton>
            </Box>
            <AMPModal
              handleClose={handleShowEditModal(false)}
              open={showEditModal}
              title={intl.formatMessage({id: "editBusiness"})}
              type="md"
              fullWidth
              withoutPaddings
            >
              <EditBusinessForm
                businessData={data}
                handleShowModal={handleShowEditModal}
              />
            </AMPModal>
            <Box sx={detailsTitleSx}>
              <Typography variant="subtitle1">
                <FormattedMessage id="businessDetails" />
              </Typography>
            </Box>
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={12}
                sm={7}
                sx={detailItemSx}
              >
                <Typography
                  variant="caption"
                >
                  <FormattedMessage id="email" />
                </Typography>
                <Typography
                  variant="subtitle2"
                >
                  {data?.businessOwnerInfo?.emailAddress}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={5}
                sx={detailItemSx}
              >
                <Typography
                  variant="caption"
                >
                  <FormattedMessage id="locations" />
                </Typography>
                <Typography
                  variant="subtitle2"
                >
                  {`${locations.data?.content.length} ${intl.formatMessage({id: 'locations'})}`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={7}
                sx={detailItemSx}
              >
                <Typography
                  variant="caption"
                >
                  <FormattedMessage id="phone" />
                </Typography>
                <Typography
                  variant="subtitle2"
                >
                  {data?.businessOwnerInfo?.phoneNumber}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={5}
                sx={detailItemSx}
              >
                <Typography
                  variant="caption"
                >
                  <FormattedMessage id="address" />
                </Typography>
                <Typography
                  variant="subtitle2"
                >
                  {`${data.address.country}, ${data.address.city}, ${data.address.street}, ${data.address.houseNumber}` }
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={7}
                sx={detailItemSx}
              >
                <Typography
                  variant="caption"
                >
                  <FormattedMessage id="role" />
                </Typography>
                <ColorField label={RoleEnum.BUSINESS} />
              </Grid>
              <Grid
                item
                xs={12}
                sm={5}
                sx={detailItemSx}
              >
							
                <Typography
                  variant="caption"
                >
                  <FormattedMessage id="urlOfConnection" />
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={detailCellSx}
                >
                  <FormattedMessage
                    id="businessUrl"
                    values={{
                      uuid: data.uuid
                    }} />
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <BusinessResetWrapper data={data} />
    </Box>
  );
};
